import cn from 'classnames'
import * as React from 'preact/compat'
import type { JSX, ComponentChild } from 'preact'

import Api from '#js/api'
import { debounce } from '#js/common/common'
import { captureFetchException, loadStylesheet, sgVersion, showModal, wait } from '#js/common/helpers'

import SVG from '#components/SVG'
import Loading from '#components/Loading'
import type GameEditorModalType from '#components/GameEditor/GameEditorModal'
import type { GameEditorMode as GameEditorModeType } from '#components/GameEditor/GameEditor'

import SearchItemFaq from './search-items/SearchItemFaq'
import SearchItemUser from './search-items/SearchItemUser'
import SearchItemComment from './search-items/SearchItemComment'
import SearchItemMaterial from './search-items/SearchItemMaterial'
import SearchItemGamePage from './search-items/SearchItemGamePage'
import SearchItemCompilation from './search-items/SearchItemCompilation'

import formCss from '#css/v9/form.pcss.json'
import headerStyles from '#css/header.pcss.json'
import styles from '#css/search-panel.pcss.json'
import commonCss from '#css/v9/common.pcss.json'
import buttonsCss from '#css/v9/buttons.pcss.json'

const categories = {
  games: 'Игры',
  users: 'Пользователи',
  news: 'Новости',
  blogs: 'Блоги',
  compilations: 'Подборки игр',
  video: 'Видео',
  articles: 'Статьи',
  cheats: 'Читы',
  comments: 'Комментарии',
  faq: 'Помощь',
}

const availableSorts = {
  games: {
    relevance: 'По релевантности',
    date: 'Сначала новые',
    '-date': 'Сначала старые',
    name: 'По алфавиту',
    '-name': 'По алфавиту (обратно)'
  },
  users: {
    relevance: 'По релевантности',
    date: 'Сначала новые',
    '-date': 'Сначала старые',
    name: 'По алфавиту',
    '-name': 'По алфавиту (обратно)'
  },
  compilations: {
    relevance: 'По релевантности',
    rating: 'По рейтингу',
    date: 'Сначала новые',
    '-date': 'Сначала старые'
  },
}

export interface SearchItem {
  id: number,
  title: string,
  url: string,
  poster?: string,
  square_poster?: string,
  type?: string,
  platforms?: Array<{ id: number, code: string, title: string, altTitles: string[] }>,
  date?: string,
  icon?: string,
  sectionName?: string,
  avatar?: string,
  rating?: number,
  comments?: number,
  inTeam?: boolean,
  text?: string,
  isBanned?: boolean,
  status?: number | string,
  games_count?: number,
  games_years?: string,
  description?: string,
  gameImages?: string[],
  isFavourite?: boolean,
  canVote?: boolean,
  userVote?: '+' | '-',
  user?: {
    avatar: string,
    name: string,
    inTeam: boolean,
    isBlogWinner: boolean
  },
  dataType?: string,
  views?: number,
  solved?: boolean
}

export interface SearchItemProps {
  item: SearchItem,
  isLoggedIn?: boolean
}

interface SearchPanelProps {
  close: () => void,
  currentTab?: keyof typeof categories
}

interface SearchPanelState {
  term: string,
  category: keyof typeof categories,
  activeCategory: keyof typeof categories,
  results: Array<SearchItem>,
  total: {
    [key: string]: number | string
  },
  isEmpty: boolean,
  loading: boolean,
  easterEgg: {
    link: string,
    img: string
  } | null,
  isLoggedIn: boolean,
  isFinal: boolean,
  sort: string,
  lastResults: number
}

// noinspection JSUnusedGlobalSymbols
export default class SearchPanel extends React.Component<SearchPanelProps, SearchPanelState> {
  state: SearchPanelState = {
    term: '',
    category: this.props.currentTab ?? 'games',
    activeCategory: this.props.currentTab ?? 'games',
    results: [],
    total: {},
    isEmpty: true,
    loading: false,
    easterEgg: null,
    isLoggedIn: false,
    isFinal: false,
    sort: 'relevance',
    lastResults: 0
  }

  input = React.createRef<HTMLInputElement>()
  overlay = React.createRef<HTMLDivElement>()
  scrollContainer = React.createRef<HTMLDivElement>()
  timeout = null

  inputChange: JSX.GenericEventHandler<HTMLInputElement> = (event) => {
    const term = event.currentTarget.value
    let easterEgg: {
      link: string,
      img: string
    } | null = null;
    if (term.toLowerCase() === 'shark' || term.toLowerCase() === 'акула') {
      const sharks = [
        {
          link: '/user/dotterian',
          img: '/img/shark.png'
        },
        {
          link: '/user/sheojo',
          img: '/img/shark2.png'
        }
      ]

      easterEgg = sharks[Math.floor(Math.random() * sharks.length)]
    } else if (term.toLowerCase() === 'плов') {
      easterEgg = {
        link: '/user/finoz26',
        img: '/img/rice.png'
      }
    }

    this.setState({
      term,
      easterEgg
    })
  }

  componentWillMount() {
    Promise.all([
      loadStylesheet('v9/cards/game-compilation-card'),
      loadStylesheet('v9/buttons'),
      loadStylesheet('v9/form')
    ])
      .then(() => wait(100))
      .then(() => {
        if (window.matchMedia('(max-width: 421px)').matches) {
          this.input.current.focus()
        } else {
          const searchInputDesktop = document.querySelector<HTMLInputElement>('[data-search-input]');
          searchInputDesktop.focus()
          searchInputDesktop.addEventListener('input', this.inputChange)
          searchInputDesktop.addEventListener('keydown', this.inputKeyDown)
        }
      })
  }

  componentDidMount() {
    window.addEventListener('keydown', this.closeOnEsc)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.closeOnEsc)
    if (!window.matchMedia('(max-width: 421px)').matches) {
      const searchInputDesktop = document.querySelector<HTMLInputElement>('[data-search-input]');
      searchInputDesktop.removeEventListener('input', this.inputChange)
      searchInputDesktop.removeEventListener('keydown', this.inputKeyDown)
    }
  }

  closeOnEsc = (event: KeyboardEvent) => {
    const key = event.key
    if (key === 'Escape') {
      this.props.close()
    }
  }

  switchCategory = (category: keyof typeof categories) => {
    this.setState({
      category,
      sort: 'relevance'
    })
  }

  performSearch = (_func: Parameters<typeof this.performSearch>, _timer: number, _immediate: boolean, offset: number = 0) => {
    const { term, category } = this.state

    if (term.length < 1) {
      this.setState({
        results: [],
        total: {},
        isEmpty: true,
        loading: false,
        lastResults: 0
      })
      return
    }

    const state: Partial<SearchPanelState> = {
      loading: true
    }

    if (offset === 0) {
      state.results = []
      state.isFinal = false
    }

    this.setState(state)
    const searchParams = new URLSearchParams()
    searchParams.set('term', encodeURIComponent(term))
    searchParams.set('offset', `${offset}`)
    searchParams.set('sort', this.state.sort)
    Api.get({ url: `search/${category}/?${searchParams.toString()}` })
      .then(({ results, total, isLoggedIn, isFinal }) => {
        const state: Partial<SearchPanelState> = {
          isLoggedIn,
          activeCategory: this.state.category,
          loading: false,
          isFinal,
        }
        if (offset > 0) {
          state.results = [...this.state.results, ...results]
        } else {
          state.results = results
          state.total = total
          try {
            window.ym(36906115, 'reachGoal', 'search', {
              type: category
            })
          } catch {
            // do nothing
          }
        }
        state.isEmpty = typeof state.results === 'undefined' || state.results.length === 0
        state.lastResults = state.results.length
        this.setState(state)
      })
      .catch(e => {
        this.setState({
          results: [],
          total: {},
          isEmpty: true,
          loading: false,
          lastResults: 0
        })
        captureFetchException(e)
      })
  }

  search = debounce(this.performSearch, 500, false)

  componentDidUpdate(_previousProps: Readonly<{}>, previousState: Readonly<SearchPanelState>, _snapshot: any) {
    if (previousState.term !== this.state.term || previousState.category !== this.state.category || previousState.sort !== this.state.sort) {
      this.search()
    }
  }

  getSearchItemComponent = () => {
    switch (this.state.activeCategory) {
      case 'games':
        return SearchItemGamePage;
      case 'users':
        return SearchItemUser;
      case 'faq':
        return SearchItemFaq;
      case 'comments':
        return SearchItemComment;
      case 'compilations':
        return SearchItemCompilation;
      default:
        return SearchItemMaterial;
    }
  }

  pluralize(words: [string, string, string]) {
    return (count: number) => {
      const cases = [2, 0, 1, 1, 1, 2];
      return count + ' ' + words[(count % 100 > 4 && count % 100 < 20) ? 2 : cases[Math.min(count % 10, 5)]];
    }
  }

  typeUrl = () => {
    return `/search?s=${encodeURIComponent(this.state.term)}&where=${this.state.activeCategory}`
  }

  inputKeyDown = (e: React.TargetedEvent<HTMLInputElement> & KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey && window.matchMedia('(min-width: 420px)').matches) {
      e.preventDefault()
      document.querySelector<HTMLButtonElement>(`.${headerStyles['header__search-btn']}`).focus()
    }
  }

  overlayClick = (e: React.TargetedEvent<HTMLElement>) => {
    if (e.target === this.overlay.current) {
      this.props.close()
    }
  }

  suggestGame = () => {
    this.props.close()
    Promise.all([
      import(`/dist/js/components/GameEditor/GameEditorModal.js?${sgVersion()}`),
      import(`/dist/js/components/GameEditor/GameEditor.js?${sgVersion()}`)
    ])
      .then(([GameEditorModalModule, GameEditorModule]: [
        { default: typeof GameEditorModalType },
        { GameEditorMode: typeof GameEditorModeType }
      ]) => {
        const { default: GameEditorModal } = GameEditorModalModule
        const { GameEditorMode } = GameEditorModule
        showModal(GameEditorModal, {
          game_id: 'new',
          mode: GameEditorMode.suggest_game
        })
      })
  }

  setSort = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      sort: e.currentTarget.value
    })
  }

  onScroll = (e: React.TargetedEvent<HTMLDivElement>) => {
    const { loading, results, total, activeCategory, isFinal } = this.state
    const bottomOffset = e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight
    let totalItems = total[activeCategory]
    if (typeof totalItems === 'string') {
      totalItems = parseInt(totalItems)
    }
    if (bottomOffset < 100 && !loading && results.length < totalItems && !isFinal) {
      // noinspection IncorrectFormatting
      this.performSearch(() => {}, 500, false, results.length)
    }
  }

  scrollContent = (e: React.TargetedEvent<HTMLDivElement> & WheelEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (this.state.loading || this.state.isEmpty || this.state.results.length <= 0) {
      return
    }

    const innerScroll = this.scrollContainer.current

    const hasVerticalScroll = innerScroll.scrollHeight > innerScroll.clientHeight
    if (!hasVerticalScroll) {
      return
    }

    const deltaY = e.deltaY
    const isScrollingDown = deltaY > 0
    const isScrollingUp = deltaY < 0
    const isAtTop = innerScroll.scrollTop <= 0
    const isAtBottom = innerScroll.scrollTop + innerScroll.clientHeight >= innerScroll.scrollHeight

    if ((isScrollingDown && !isAtBottom) || (isScrollingUp && !isAtTop)) {
      e.preventDefault()
      innerScroll.scrollTo(0, innerScroll.scrollTop + deltaY)
    }
  }

  render(): ComponentChild {
    const {
      term,
      category,
      isEmpty,
      results,
      activeCategory,
      loading,
      total,
      easterEgg,
      isLoggedIn,
      lastResults
    } = this.state

    const categoryButtons = []

    for (let value in categories) {
      const totalNumber = total[value] || 0

      categoryButtons.push(<li>
        <span
          className={cn({
            [`${commonCss['tabs__item']}`]: true,
            [`${commonCss['tabs__item--active']}`]: value === category,
          })}
          onClick={() => this.switchCategory(value as keyof typeof categories)}
        >
          <span>{categories[value]}</span>
          {+totalNumber > 0 && <sup>{totalNumber}</sup>}
        </span>
      </li>)
    }

    let resultsItems = [];
    const panelClasses = [styles['search-panel']]

    const scrollClasses = [styles['scroll-inner']]

    if (!isEmpty && results.length > 0) {
      if (['games', 'users', 'compilations'].includes(activeCategory)) {
        scrollClasses.push(styles[`scroll-inner--${activeCategory}`])
      }
      const SearchItem = this.getSearchItemComponent();
      if (['compilations'].includes(activeCategory)) {
        resultsItems = results.flatMap(item => [
          <SearchItem key={item.id} item={item} isLoggedIn={isLoggedIn}/>,
          <hr className={commonCss['separator']}/>
        ])
      } else {
        resultsItems = results.map(item => <SearchItem key={item.id} item={item} isLoggedIn={isLoggedIn}/>)
      }
      panelClasses.push(styles['search-panel--open'])
    }

    const sortOptions = []
    const categorySorts = availableSorts[this.state.category] ?? {
      relevance: 'По релевантности',
      date: 'Сначала новые',
      '-date': 'Сначала старые'
    }
    for (let value in categorySorts) {
      const label = categorySorts[value]
      sortOptions.push(<option
        value={value}
        selected={value === this.state.sort}
      >{label}</option>)
    }

    return <div className={styles['overlay']} ref={this.overlay} onClick={this.overlayClick}>
      <div className={panelClasses.join(' ')} onWheel={this.scrollContent}>
        <div className={styles['content-wrap']}>
          <button className={styles['close-btn']} data-plugins='search-panel'>
            <SVG i='sg/close'/>
          </button>
          <div
            className={cn({
              [`${styles['search-input']}`]: true,
              [`${styles['search-input--active']}`]: term.length > 0,
            })}
          >
            <SVG i='sg/search' className={styles['search-input__icon']}/>
            <input
              type='text'
              ref={this.input}
              value={term}
              onInput={this.inputChange}
              onKeyDown={this.inputKeyDown}
              className={styles['search-input__input']}
              placeholder='ПОИСК'
            />
          </div>
          <div
            className={commonCss['tabs-container']}
            data-plugins="horizontal-scroll"
            style='flex-shrink: 0; grid-area: menu;'
          >
            <div className={commonCss['tabs']}>
              {categoryButtons}
            </div>
          </div>
          {lastResults > 0 && <div className={styles['sort-row']}>
            <select onChange={this.setSort} className={formCss['input']} style='width: auto'>
              {sortOptions}
            </select>
            {category === 'games' && <div style='display: flex; column-gap: 16px; align-items: center'>
              <button
                type='button'
                className={cn(buttonsCss['button'], buttonsCss['text-button'])}
                onClick={this.suggestGame}
                style='--button-fg: var(--v9-color-text-header)'
              >
                <SVG i="sg-new/plus" adaptive/>
                Добавить игру
              </button>
              <a
                href='/games/catalog'
                className={cn(buttonsCss['button'], buttonsCss['text-button'])}
                style='--button-fg: var(--v9-color-text-header)'
              >
                <SVG i="sg-new/filters" adaptive/>
                Расширенный поиск
              </a>
            </div>}
          </div>}
          {loading && results.length === 0 && <div className={styles['scroll-container']}>
            <div style={{ marginTop: '32px' }}>
              <Loading string='Ищем…'/>
            </div>
          </div>}
          {!loading && isEmpty &&
            <div className={styles['scroll-container']}>
              <div style='display: flex; flex-direction: column; align-items: center;'>
                {category === 'games'
                  ? <>
                    <p style="color: var(--v9-color-text-gray); text-align: center;">
                      Не можешь найти игру? <wbr/>Заполни&nbsp;запрос&nbsp;на&nbsp;добавление&nbsp;в&nbsp;базу
                    </p>
                    <div style='display: flex; align-items: center; column-gap: 16px; flex-wrap: wrap; row-gap: 16px; justify-content: center'>
                      <button
                        type='button'
                        className={cn(buttonsCss['button'], buttonsCss['text-button'])}
                        onClick={this.suggestGame}
                        style='--button-fg: var(--v9-color-text-header)'
                      >
                        <SVG i="sg-new/plus" adaptive/>
                        Добавить игру
                      </button>
                      <a
                        href='/games/catalog'
                        className={cn(buttonsCss['button'], buttonsCss['text-button'])}
                        style='--button-fg: var(--v9-color-text-header)'
                      >
                        <SVG i="sg-new/filters" adaptive/>
                        Расширенный поиск
                      </a>
                    </div>
                    <p style="color: var(--v9-color-text-gray); text-align: center;">
                      Не помнишь название? <wbr/>
                      <a
                        href='/faq/searching-for-game'
                        className={commonCss['link']}
                        style='--link-color: var(--v9-color-text-header); white-space: nowrap'
                      >
                        Спроси у пользователей StopGame
                      </a>
                    </p>
                  </>
                  : 'Ничего не найдено'
                }
              </div>
            </div>
          }
          {!isEmpty && results.length > 0 && <>
            <div ref={this.scrollContainer} onScroll={this.onScroll} className={styles['scroll-container']}>
              <div className={scrollClasses.join(' ')}>
                {resultsItems}
              </div>
              {loading && <div style={{ marginTop: '32px' }}>
                <Loading string='Ищем…'/>
              </div>}
            </div>
          </>}
        </div>
        {easterEgg && <a href={easterEgg.link} target='_blank' className={styles['shark']}>
          <img alt='' src={easterEgg.img}/>
        </a>}
      </div>
    </div>
  }
}
